import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import FollowUpEmailComposer from './FollowUpEmailComposer';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

const FollowUpNotePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { recommendation, recommendationId, bragSheetData, fromBragSheet, viewMode, fromHub } = location.state || {};

  const [fullRecommendation, setFullRecommendation] = useState(recommendation || null);
  const [loading, setLoading] = useState(!recommendation);

  useEffect(() => {
    // If we don't have recommendation data, load it
    if (!recommendation && recommendationId) {
      loadRecommendation();
    }
  }, [recommendation, recommendationId]);

  const loadRecommendation = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('college_recommendation_letters')
        .select('*')
        .eq('id', recommendationId)
        .single();

      if (error) throw error;

      setFullRecommendation(data);
    } catch (error) {
      console.error('Error loading recommendation:', error);
      toast.error('Failed to load recommendation data');
      navigate('/college/recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (fromBragSheet) {
      // If coming from brag sheet, go back to recommendations hub
      navigate('/college/recommendations');
    } else if (fromHub) {
      // If coming from hub, go back to hub
      navigate('/college/recommendations');
    } else {
      // Default: go back
      navigate(-1);
    }
  };

  const handleSave = async (emailContent) => {
    // Update workflow state to note_generated
    try {
      const { error } = await supabase
        .from('college_recommendation_letters')
        .update({
          brag_sheet_workflow_state: 'note_generated',
          follow_up_email_content: emailContent,
          follow_up_email_date: new Date().toISOString()
        })
        .eq('id', recommendationId);

      if (error) throw error;

      toast.success('Follow-up note saved successfully!');
    } catch (error) {
      console.error('Error updating workflow state:', error);
      // Don't show error to user - the note was still saved
    }
  };

  const handleSend = async (emailContent) => {
    // Update workflow state to note_generated and mark as sent
    try {
      const { error } = await supabase
        .from('college_recommendation_letters')
        .update({
          brag_sheet_workflow_state: 'note_generated',
          follow_up_email_content: emailContent,
          follow_up_email_date: new Date().toISOString(),
          follow_up_sent: true
        })
        .eq('id', recommendationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating workflow state:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-azure-500 mx-auto mb-4"></div>
          <p className="text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={handleClose}
            className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-white" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {viewMode ? 'View Follow-Up Note' : 'Create Follow-Up Note'}
            </h1>
            <p className="text-slate-600 text-sm">
              For {fullRecommendation?.recommender_name} • {fullRecommendation?.subject || fullRecommendation?.recommender_type}
            </p>
          </div>
        </div>

        {/* Helper Info */}
        {fromBragSheet && !viewMode && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-emerald-800">
              <strong>Great work!</strong> Your brag sheet is ready. Now create a follow-up note to send to {fullRecommendation?.recommender_name} with all the materials they need to write your recommendation.
            </p>
          </div>
        )}

        {/* Email Composer */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <FollowUpEmailComposer
            recommendation={fullRecommendation}
            isOpen={true}
            onClose={handleClose}
            onSave={handleSave}
            onSend={handleSend}
          />
        </div>
      </div>
    </div>
  );
};

export default FollowUpNotePage;
